import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import { VerticalFlex } from '@components/RecordCard/RecordCard.style';

import * as ROUTES from '@constants/routes';

import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useSession } from '@hooks/useSession';

import { ColumnLayout } from '@layouts/DrilldownStyle';
import { PageBody } from '@layouts/DrilldownStyle';

import { DiagnosticReportRecord } from '@models/diagnostic-report';
import { ObservationRecord } from '@models/observation';

import type { ResourceAssociationUnion } from 'generated/types';

import CustomToolbar from './CustomToolbar';
import RelatedRecordsButton from './RelatedRecordsButton';

const messages = defineMessages({
  related: {
    id: 'relatedRecordsTable.related',
    defaultMessage: 'Related',
  },
  appointmentHistory: {
    id: 'relatedRecordsTable.appointmentHistory',
    defaultMessage: 'Appointments',
  },
  emergencyVisits: {
    id: 'relatedRecordsTable.emergencyVisits',
    defaultMessage: 'Emergency Visits',
  },
  hospitalizations: {
    id: 'relatedRecordsTable.hospitalizations',
    defaultMessage: 'Hospitalizations',
  },
  vitalSigns: {
    id: 'relatedRecordsTable.vitalSigns',
    defaultMessage: 'Vital Signs',
  },
  geneticInformation: {
    id: 'relatedRecordsTable.geneticInformation',
    defaultMessage: 'Genetic Information',
  },
  labResults: {
    id: 'relatedRecordsTable.labResults',
    defaultMessage: 'Lab Results',
  },
  fetchError: {
    id: 'relatedRecordsTable.fetchError',
    defaultMessage: 'There was an error fetching related records',
  },
  unknown: {
    id: 'relatedRecordsTable.unknown',
    defaultMessage: 'Unknown Record',
  },
  imagingTests: {
    id: 'relatedRecordsTable.imagingTests',
    defaultMessage: 'Imaging and Tests',
  },
  contacts: {
    id: 'relatedRecordsTable.contacts',
    defaultMessage: 'Contacts',
  },
  allergies: {
    id: 'relatedRecordsTable.allergies',
    defaultMessage: 'Allergies',
  },
  immunizations: {
    id: 'relatedRecordsTable.immunizations',
    defaultMessage: 'Immunizations',
  },
  familyHistory: {
    id: 'relatedRecordsTable.familyHistory',
    defaultMessage: 'Family History',
  },
  medications: {
    id: 'relatedRecordsTable.medications',
    defaultMessage: 'Medications',
  },
  surgeries: {
    id: 'relatedRecordsTable.surgeries',
    defaultMessage: 'Surgeries',
  },
  medicalConditions: {
    id: 'relatedRecordsTable.medicalConditions',
    defaultMessage: 'Medical Conditions',
  },
  body: {
    id: 'relatedRecordsTable.body',
    defaultMessage: 'Body Observations',
  },
});

export default function RelatedRecordsTable({
  records = [],
}: {
  /**
   * Current record's related records
   */
  records: ResourceAssociationUnion[] | null | undefined;
}) {
  const { formatMessage } = useIntl();
  const { flags } = useFeatureFlags();
  const {
    state: { isClient, isCurator },
  } = useSession();
  const canAddOrDeleteRelatedRecord =
    flags.isRecordCreateAssociationEnabled ||
    flags.isRecordDeleteAssociationEnabled;

  const tableData = records?.map((record) => {
    if (record.__typename === 'AllergyModel') {
      return {
        displayName: `${formatMessage(messages.allergies)}: ${
          record.displayName
        }`,
        route: `${ROUTES.ALLERGIES}${record.id}`,
      };
    }
    if (record.__typename === 'AppointmentModel') {
      return {
        displayName: `${formatMessage(messages.appointmentHistory)}: ${
          record.displayName
        }`,
        route: `${ROUTES.APPOINTMENT_HISTORY}${record.id}`,
      };
    }
    if (record.__typename === 'ProcedureModel') {
      return {
        displayName: `${formatMessage(messages.surgeries)}: ${
          record.displayName
        }`,
        route: `${ROUTES.SURGERIES}${record.id}`,
      };
    }
    if (record.__typename === 'EncounterModel') {
      if (record.class.code === 'emergency') {
        return {
          displayName: `${formatMessage(messages.emergencyVisits)}: ${
            record.displayName
          }`,
          route: `${ROUTES.EMERGENCY_VISITS}${record.id}`,
        };
      }
      if (record.class.code === 'hospitalization') {
        return {
          displayName: `${formatMessage(messages.hospitalizations)}: ${
            record.displayName
          }`,
          route: `${ROUTES.HOSPITALIZATIONS}${record.id}`,
        };
      }
    }
    if (record.__typename === 'ImmunizationModel') {
      return {
        displayName: `${formatMessage(messages.immunizations)}: ${
          record.displayName
        }`,
        route: `${ROUTES.IMMUNIZATIONS}${record.id}`,
      };
    }
    if (record.__typename === 'ConditionModel') {
      return {
        displayName: `${formatMessage(messages.medicalConditions)}: ${
          record.displayName
        }`,
        route: `${ROUTES.MEDICAL_CONDITIONS}${record.id}`,
      };
    }
    if (record.__typename === 'MedicationStatementModel') {
      return {
        displayName: `${formatMessage(messages.medications)}: ${
          record.displayName
        }`,
        route: `${ROUTES.MEDICATIONS}${record.id}`,
      };
    }
    if (record.__typename === 'DiagnosticReportModel') {
      let route = ROUTES.IMAGING_TESTS;
      const report = new DiagnosticReportRecord(record);
      const isLabs = report.hasCategory('laboratory');
      if (isLabs) {
        route = ROUTES.LAB_RESULTS;
        return {
          displayName: `${formatMessage(messages.labResults)}: ${
            record.displayName
          }`,
          route: `${route}${record.id}`,
        };
      }
      return {
        displayName: `${formatMessage(messages.imagingTests)}: ${
          record.displayName
        }`,
        route: `${route}${record.id}`,
      };
    }
    if (record.__typename === 'ObservationModel') {
      const observation = new ObservationRecord(record);
      const isVitalSigns = observation.hasCategory('vital-signs');
      const isGenetics = observation.hasCategory('genetic-information');
      const isBody = observation.hasCategory('body');
      if (isVitalSigns) {
        return {
          displayName: `${formatMessage(messages.vitalSigns)}: ${
            record.displayName
          }`,
          route: `${ROUTES.VITAL_SIGNS}${observation.code}`,
        };
      }
      if (isGenetics) {
        return {
          displayName: `${formatMessage(messages.geneticInformation)}: ${
            record.displayName
          }`,
          route: `${ROUTES.GENETIC_INFORMATION}${record.id}`,
        };
      }
      if (isBody) {
        return {
          displayName: `${formatMessage(messages.body)}: ${record.displayName}`,
          route: `${ROUTES.BODY}${observation.code}`,
        };
      }
      return {
        displayName: `${record.displayName}`,
        route: null,
      };
    }
    if (record.__typename === 'FamilyMemberHistoryModel') {
      return {
        displayName: `${formatMessage(messages.familyHistory)}: ${
          record.displayName
        }`,
        route: `${ROUTES.FAMILY_HISTORY}${record.id}`,
      };
    }
    return {
      displayName: 'Unknown',
      route: null,
    };
  });

  // eslint-disable-next-line no-nested-ternary
  return tableData?.length ? (
    <PageBody padding="8px 5px 0 0">
      <MuiDataTable
        columns={[
          {
            name: 'displayName',
            className: 'blue-hover',
            options: {
              customHeadLabelRender: () => null,
              customHeadRender: () => null,
            },
          },
        ]}
        data={tableData}
        options={{
          onRowClick: (_rowData, { dataIndex }) => {
            if (dataIndex <= tableData.length - 1) {
              const row = tableData[dataIndex];
              if (row.route) {
                navigate(row.route);
              }
            }
          },
          sortOrder: {
            name: 'displayName',
            direction: 'asc',
          },
          pagination: false,
          customFooter: () => null,
          customToolbar: () => {
            if (isCurator) {
              return <CustomToolbar />;
            }
            return null;
          },
          sort: false,
        }}
        themeOptions={{
          components: {
            MUIDataTable: {
              styleOverrides: {
                // @ts-expect-error Class missing from type def
                responsiveBase: {
                  background: 'white',
                  borderRadius: '5px',
                },
                paper: {
                  width: '100%',
                  padding: 0,
                  margin: 0,
                },
              },
            },
            MUIDataTableBodyCell: {
              styleOverrides: {
                root: {
                  maxWidth: '1000px !important',
                },
              },
            },
          },
        }}
        title={formatMessage(messages.related)}
      />
    </PageBody>
  ) : isClient ? null : (
    <PageBody padding="22px 15px 22px 22px">
      <ColumnLayout>
        <VerticalFlex alignItems="flex-start">
          <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
            {formatMessage(messages.related)}
          </div>
        </VerticalFlex>
        {canAddOrDeleteRelatedRecord && (
          <VerticalFlex alignItems="flex-end">
            <RelatedRecordsButton />
          </VerticalFlex>
        )}
      </ColumnLayout>
    </PageBody>
  );
}
